export const BILLING_CYCLES = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'half_yearly', label: 'Half-yearly' },
  { key: 'yearly', label: 'Yearly' },
];

export const CYCLE_MULTIPLIERS = {
  monthly: 1,
  quarterly: 3,
  half_yearly: 6,
  yearly: 12,
};

export function syncDefaultPrices(monthlyDefault) {
  const monthly = Math.max(0, round2(Number(monthlyDefault) || 0));
  return {
    default_price_monthly: monthly,
    default_price_half_yearly: round2(monthly * 6),
    default_price_yearly: round2(monthly * 12),
  };
}

export function round2(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export function computeSavings(defaultPrice, sellingPrice) {
  const defaultVal = round2(defaultPrice);
  const sellingVal = round2(sellingPrice);
  const amountSaved = round2(Math.max(0, defaultVal - sellingVal));
  let discountPercent = 0;
  if (defaultVal > 0 && amountSaved > 0) {
    discountPercent = round2((amountSaved / defaultVal) * 100);
  }
  return { discount_percent: discountPercent, amount_saved: amountSaved };
}

export function defaultPriceForCycle(plan, cycle) {
  if (!plan) return 0;
  const key = `default_price_${cycle}`;
  if (plan[key] != null && plan[key] !== '') {
    return round2(plan[key]);
  }
  const monthlyDefault = round2(plan.default_price_monthly ?? plan.price_monthly ?? 0);
  const multiplier = CYCLE_MULTIPLIERS[cycle] ?? 1;
  return round2(monthlyDefault * multiplier);
}

export function sellingPriceForCycle(plan, cycle) {
  if (!plan) return 0;
  const key = `price_${cycle}`;
  return round2(plan[key] ?? 0);
}

export function computeSaveExtra(plan, cycle) {
  if (cycle === 'monthly') return 0;
  const multiplier = CYCLE_MULTIPLIERS[cycle] ?? 0;
  if (multiplier <= 1) return 0;
  const monthlySelling = sellingPriceForCycle(plan, 'monthly');
  const cycleSelling = sellingPriceForCycle(plan, cycle);
  const expectedMonthlyCost = round2(monthlySelling * multiplier);
  return round2(Math.max(0, expectedMonthlyCost - cycleSelling));
}

export function pricingForCycle(plan, cycle) {
  if (plan?.pricing?.[cycle]) {
    return plan.pricing[cycle];
  }
  const defaultPrice = defaultPriceForCycle(plan, cycle);
  const sellingPrice = sellingPriceForCycle(plan, cycle);
  const savings = computeSavings(defaultPrice, sellingPrice);
  return {
    billing_cycle: cycle,
    default_price: defaultPrice,
    selling_price: sellingPrice,
    discount_percent: savings.discount_percent,
    amount_saved: savings.amount_saved,
    save_extra: computeSaveExtra(plan, cycle),
  };
}

export function pricingForAllCycles(plan) {
  return BILLING_CYCLES.reduce((acc, { key }) => {
    acc[key] = pricingForCycle(plan, key);
    return acc;
  }, {});
}

export function formatDiscountBadge(percent) {
  const rounded = Math.round(Number(percent) || 0);
  return `${rounded}% OFF`;
}

export function cycleSuffix(cycle) {
  const map = {
    monthly: '/month',
    quarterly: '/quarter',
    half_yearly: '/6 months',
    yearly: '/year',
  };
  return map[cycle] || '';
}

export function formatBillingCycleLabel(cycle) {
  const found = BILLING_CYCLES.find((c) => c.key === cycle);
  if (found) return found.label;
  return (cycle || '').replace(/_/g, ' ');
}

export function validatePricingForm(form) {
  const errors = [];
  BILLING_CYCLES.forEach(({ key, label }) => {
    const defaultPrice = defaultPriceForCycle(form, key);
    const sellingPrice = sellingPriceForCycle(form, key);
    if (sellingPrice > defaultPrice + 0.001) {
      errors.push(`Selling price cannot exceed default price for ${label}.`);
    }
  });
  return errors;
}

export function applyMonthlyDefaultChange(form, monthlyDefault) {
  const synced = syncDefaultPrices(monthlyDefault);
  return { ...form, ...synced };
}

export function buildPricingPayload(form) {
  const synced = syncDefaultPrices(form.default_price_monthly ?? form.price_monthly ?? 0);
  return {
    default_price_monthly: synced.default_price_monthly,
    default_price_half_yearly: synced.default_price_half_yearly,
    default_price_yearly: synced.default_price_yearly,
    price_monthly: round2(form.price_monthly),
    price_half_yearly: round2(form.price_half_yearly),
    price_yearly: round2(form.price_yearly),
    price_quarterly: 0,
    default_price_quarterly: 0,
  };
}
